<template>
  <div>
    <v-data-table
      :headers="subscriberHeaders"
      :items="subscription.subscribers.collection"
      :options.sync="options"
      :loading="!subscribersLoaded"
      :hide-default-footer="hideTableFooter"
      :loading-text="$t('Loading...')"
      class="elevation-1"
      item-key="@id"
    >
      <template v-slot:top>
        <v-toolbar flat>
          <v-toolbar-title>{{ $t("Subscribers") }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn
            @click="refreshSubscriberData()"
            :title='$t("refreshData")'
            fab
            icon
            elevation="2"
            small
          >
            <v-icon>mdi-refresh</v-icon>
          </v-btn>
          <v-btn
            v-if="hasRole('ROLE_CREATE_SUBSCRIBER')"
            :disabled="locked"
            @click="createOrEditSubscriberHandler(null)"
            color="success"
            style="margin-left: 20px"
          >
            {{ $t('AddSubscriber') }}
          </v-btn>

          <template v-if="subscription && subscription.id">
            <SubscriberDialog
              v-if="hasRole('ROLE_UPDATE_SUBSCRIBER') || hasRole('ROLE_CREATE_SUBSCRIBER')"
              v-model="showSubscriberDialogForm"
              :subscriber-item="subscriberFormItem"
              :handle-created="onCreated"
              :handle-updated="onUpdated"
              :show-handle="showSubscriberDialogForm"
              :subscriptionId="subscription.id"
              :current-subscribers="subscription.subscribers.collection"
              :title="$t('AddSubscriber')"
              @close="subscriberDialogControl(true)"
            />
          </template>
        </v-toolbar>
      </template>
      <template v-slot:item.name="{ item }">
        <template v-if="item.customer">
          <template v-if="item.customer.isBusiness">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  v-bind="attrs"
                  v-on="on"
                  class="mr-2"
                >
                  mdi-domain
                </v-icon>
              </template>
              <span>Firmen-Account</span>
            </v-tooltip>
            {{ item.customer.profile.company }}
          </template>
          <template v-else>
            {{ item.customer.profile.firstName }} {{ item.customer.profile.lastName }}
          </template>
        </template>
        <template v-else>
          {{ item.name }}
          <template v-if="isInhouse">{{ item.email }}</template>
        </template>
      </template>
      <template v-slot:item.customerNumber="{ item }">
        <v-btn
          plain
          color="primary"
          @click="openCustomerDialog(item.customer)"
          style="padding: 0"
        >
          {{ item.customer.customerNumber }}
        </v-btn>
      </template>
      <template v-slot:item.invoiceState="{ item }">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-icon
              v-bind="attrs"
              v-on="on"
              class="mr-2"
            >
              <template v-if="item.invoice">
                <template v-if="item.invoice.paid">mdi-receipt-text-check-outline</template>
                <template v-else>mdi-email-check-outline</template>
              </template>
              <template v-else>mdi-email-remove-outline</template>
            </v-icon>
          </template>
          <span>
            <template v-if="item.invoice">
              <template v-if="item.invoice.paid">Bezahlt</template>
              <template v-else>Erstellt & versendet</template>
              ({{ item.invoice.number }})
            </template>
            <template v-else>Nicht erstellt & versendet</template>
          </span>
        </v-tooltip>
      </template>
      <template v-slot:item.registrationDate="{ item }">
        {{ formatDateTime(item.createdAt, "L") }}
      </template>
      <template v-slot:no-data>
        {{ $t("NoSubscribersYet") }}
      </template>
      <template
        v-if="!locked"
        v-slot:item.action="{ item }"
      >
        <v-tooltip
          bottom
          v-if="hasRole('ROLE_DELETE_SUBSCRIBER')"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-icon
              v-bind="attrs"
              v-on="on"
              @click="handleDelete(item)"
              class="mr-2"
              small
            > mdi-delete </v-icon>
          </template>
          <span>{{ $t('SubscriberDelete') }}</span>
        </v-tooltip>
      </template>
    </v-data-table>
    <CustomerInfoDialog
      v-model="showCustomerDialog"
      :customer="customerDialogItem"
      :key="`customer-${customerDialogItem.id}`"
      :has-graph-ql-data="true"
      @close="showCustomerDialog = false"
    />
  </div>
</template>
<script>
  import DocumentMixin from "../../mixins/DocumentMixin";
  import NotificationMixin from "@/mixins/NotificationMixin";
  import TenantMixin from "@/mixins/TenantMixin";
  import SeminarMixin from '@/mixins/SeminarMixin';
  import { formatDateTime } from '@/utils/dates';
  import SubscriberDialog from '@/components/subscriber/Dialog';
  import CustomerInfoDialog from '@/components/customer/CustomerInfoDialog';
  import remove from 'lodash/remove';
  import gql from "graphql-tag";

  export default {
    name: "SubscriberListAndAdd",
    components: {
      SubscriberDialog,
      CustomerInfoDialog
    },
    mixins: [DocumentMixin, NotificationMixin, TenantMixin, SeminarMixin],
    props: {
      values: {
        type: Object,
        required: false,
      },
      showTitle: {
        type: Boolean,
        required: false,
        default: () => true,
      },
      locked: {
        type: Boolean,
        required: true,
      },
      subscription: {
        type: Object,
        required: true
      },
    },
    data() {
      return {
        options: {
          itemsPerPage: -1
        },
        forceLoading: false,
        showSubscriberDialogForm: false,
        subscriberFormItem: {},
        showCustomerDialog: false,
        customerDialogItem: {},
      };
    },
    computed: {
      subscriberHeaders() {
        let headerSet = [
          {
            text: this.$i18n.t("name"),
            value: "name",
            align: 'start'
          },
        ];

        headerSet.unshift({
          text: this.$i18n.t("customerNumber"),
          value: "customerNumber",
          sortable: false,
          align: 'start',
          width: '140px'
        });

        headerSet.push({
          text: this.$i18n.t("invoice"),
          value: "invoiceState",
          align: "center",
          width: '90px',
          sortable: false,
        })
        headerSet.push({
          text: this.$i18n.t("registrationDate"),
          value: "registrationDate",
          sortable: false,
          align: 'end',
          width: '140px'
        })

        headerSet.push({
          text: this.$i18n.t('Actions'),
          value: 'action',
          sortable: false,
          align: 'end'
        });

        return headerSet;
      },
      subscribersLoaded() {
        return !this.forceLoading && this.subscription.subscribers.paginationInfo.totalCount >= 0;
      },
      hideTableFooter() {
        return !this.subscription.subscribers.paginationInfo.hasNextPage
      },
    },
    methods: {
      formatDateTime,
      openCustomerDialog(customer) {
        this.customerDialogItem = customer;
        this.showCustomerDialog = true;
      },
      async refreshSubscriberData() {
        this.forceLoading = true;
        this.$emit('updateSubscribers', this.subscription.subscribers.collection);
        this.forceLoading = false;
      },
      onCreated(item) {
        this.subscription.subscribers.collection.push(item);
        this.$emit('updateSubscribers', this.subscription.subscribers.collection);
      },
      onUpdated(item) {
        this.showMessage(item['@id'] + ' updated');
      },
      createOrEditSubscriberHandler(subscribersItem) {
        this.subscriberFormItem = subscribersItem ?? this.defaultItemProps;
        this.subscriberDialogControl();
      },
      subscriberDialogControl(close = false) {
        this.showSubscriberDialogForm = !close;
      },
      handleDelete(subscriber) {
        this.$confirm(this.$t('deleteSubscriberFromSubscription'), {
          buttonTrueText: this.$t('Delete'),
          buttonFalseText: this.$t('Cancel')
        }).then((response) => {
          if (!response) {
            return;
          }
          this.deleteSubscriberHandler(subscriber);
        });
      },
      async deleteSubscriberHandler(item) {
        await this.$apollo.mutate({
          mutation: gql`mutation ($input: deleteSubscriberInput!) {
            deleteSubscriber(input: $input) {
              clientMutationId
              subscriber{
                id
              }
            }
          }
          `,
          variables: {
            input: {
              id: item.id
            },
          },
          context: {
            headers: {
              authorization: this.$store.state.authentication.loggedIn ? `Bearer ${this.$store.state.authentication.token}` : ''
            }
          }
        }).catch((error) => {
          // Error
          console.error(error)
        });
        let cleaned = remove(this.subscription.subscribers.collection, (subscriber) => {
          return !(subscriber.id === item.id);
        });
        this.$emit('updateSubscribers', cleaned);
      },
    }
  };
</script>
